// @ts-nocheck
import { AlertTriangle } from "lucide-react";
import React from "react";

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../components/ui/tooltip";
import { useHealthScoreThresholds } from "../../../Hooks/useHealthScoreThresholds";
import {
  businessHealthClass,
  businessHealthColor,
} from "../../../Utils/Helper/businessHealth";
import { formatActualValueWithUnit } from "../../../Utils/Helper/formatHawkeyeMetricValue";

interface MetricDetail {
  name: string;
  value: number;
  weight: number;
  actualValue?: string;
  uom?: string;
}

interface CategoryDetail {
  name: string;
  score: number;
  weight: number;
}

interface BubbleCellProps {
  /** Null/undefined = no KPI data (gray “-” empty state). */
  score: number | null | undefined;
  isOverall?: boolean;
  size?: number;
  className?: string;
  onClick?: (event: React.MouseEvent) => void;
  isClickable?: boolean;
  showTooltip?: boolean;
  tooltipTitle?: string;
  metrics?: MetricDetail[];
  categories?: CategoryDetail[];
  aiScore?: number;
  divergenceWarning?: boolean;
  hasDigest?: boolean;
  /** When true, tooltip uses metric-view labels instead of KPI Score labels */
  isMetricView?: boolean;
}

export const BubbleCell: React.FC<BubbleCellProps> = ({
  score,
  isOverall = false,
  size = 32,
  className = "",
  onClick,
  isClickable = false,
  showTooltip = false,
  tooltipTitle,
  metrics = [],
  categories = [],
  aiScore,
  divergenceWarning = false,
  hasDigest = false,
  isMetricView = false,
}) => {
  const { businessHealth: bhThresholds } = useHealthScoreThresholds();

  const isNoData = score == null || !Number.isFinite(score);
  const numericScore = isNoData ? 0 : score;
  const isZeroScore = !isNoData && numericScore === 0;
  const sizeScore =
    aiScore !== undefined ? aiScore : isNoData ? 0 : numericScore;
  // Same score→size proportion as before, but cap fill inside the track the
  // way Business Health does (24/30) so the grey ring stays visible on
  // high scores instead of looking like a solid disc.
  const trackFillMax = Math.round(size * (24 / 30));
  const scoreCircleSize = isOverall
    ? size
    : Math.max(4, Math.round((sizeScore / 100) * trackFillMax));

  const scoreBand =
    !isNoData && !isZeroScore
      ? businessHealthClass(numericScore, bhThresholds)
      : null;

  // Color from Settings Business Health thresholds (R/Y/G); zero / no-data use grey
  const getScoreColor = () => {
    // No-data overall uses the same light ring bg as category/metric empty cells
    if (isNoData && isOverall) return "rgba(100, 116, 139, 0.2)"; // muted-foreground/20
    if (isNoData || isZeroScore) return "#94a3b8"; // slate-400 (Grey)
    return (
      businessHealthColor(numericScore, bhThresholds) ?? "#ef4444"
    );
  };

  const getScoreColorClass = () => {
    if (isNoData || isZeroScore) return "text-slate-500";
    if (scoreBand === "Strong") return "text-green-600";
    if (scoreBand === "Stable") return "text-yellow-600";
    return "text-red-600";
  };

  const getBubbleGlow = () => {
    if (isNoData || isZeroScore) {
      return "0 0 20px rgba(148, 163, 184, 0.25), 0 0 40px rgba(148, 163, 184, 0.1)";
    } else if (scoreBand === "Strong") {
      return "0 0 20px rgba(16, 185, 129, 0.3), 0 0 40px rgba(16, 185, 129, 0.1)";
    } else if (scoreBand === "Stable") {
      return "0 0 20px rgba(245, 158, 11, 0.3), 0 0 40px rgba(245, 158, 11, 0.1)";
    } else {
      return "0 0 20px rgba(239, 68, 68, 0.3), 0 0 40px rgba(239, 68, 68, 0.1)";
    }
  };

  // Same score → size / colour maths as before; only the chrome matches
  // Business Health (white disc + grey track + inner fill).
  const bubbleContent = (
    <div
      className={`relative flex items-center justify-center ${className} ${
        isClickable
          ? "cursor-pointer transition-transform hover:scale-110"
          : ""
      }`}
      style={{ width: size + 8, height: size + 8 }}
      onClick={onClick}
    >
      {/* Divergence pulsing ring */}
      {divergenceWarning && !isNoData && (
        <div
          className="absolute rounded-full border-2 border-amber-400 animate-pulse"
          style={{
            width: size + 4,
            height: size + 4,
          }}
        />
      )}

      {/* Opaque white disc + grey track (Business Health Bubble chrome) */}
      <span
        className="relative z-[1] flex items-center justify-center rounded-full"
        style={{
          width: isOverall ? scoreCircleSize : size,
          height: isOverall ? scoreCircleSize : size,
          backgroundColor: "#fff",
          border:
            isNoData && !isOverall ? "1.5px dashed #cbd5e1" : "none",
        }}
      >
        {isNoData && !isOverall ? null : (
          <>
            {!isOverall ? (
              <span
                aria-hidden
                className="absolute inset-0 rounded-full"
                style={{ backgroundColor: "rgba(156, 163, 175, 0.3)" }}
              />
            ) : null}
            <span
              className="relative block rounded-full"
              style={{
                width: scoreCircleSize,
                height: scoreCircleSize,
                backgroundColor: getScoreColor(),
              }}
            />
          </>
        )}
      </span>

      {/* Score text overlay for overall scores */}
      {isOverall && (
        <span
          className={`absolute text-xs font-semibold z-10 ${
            isNoData || isZeroScore ? "text-slate-500" : "text-white"
          }`}
        >
          {isNoData ? "–" : Math.round(numericScore)}
        </span>
      )}

      {/* Divergence warning icon */}
      {divergenceWarning && !isOverall && !isNoData && (
        <div className="absolute -top-1 -right-1 z-20">
          <AlertTriangle className="h-3 w-3 text-amber-500" />
        </div>
      )}
    </div>
  );

  if (!showTooltip) {
    return bubbleContent;
  }

  // Empty / missing KPI: gray “–” cell; hover shows KPIs Data not available
  if (isNoData) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>{bubbleContent}</TooltipTrigger>
          <TooltipContent
            side="top"
            align="center"
            sideOffset={10}
            className="w-72 max-w-[90vw] overflow-visible bg-white text-slate-900 p-3 border z-[120] break-words leading-relaxed"
            style={{ boxShadow: getBubbleGlow() }}
          >
            <div className="space-y-2">
              <div className="text-center">
                <div className="text-lg font-bold text-slate-500">–</div>
                <div className="text-sm font-semibold text-foreground">
                  KPIs Data not available
                </div>
                {tooltipTitle && (
                  <div className="text-xs text-muted-foreground mt-0.5">
                    {tooltipTitle}
                  </div>
                )}
              </div>
              {!isMetricView && (
                <div className="space-y-1 text-xs text-muted-foreground border-t border-border pt-1.5">
                  <div className="flex items-start justify-between gap-3">
                    <span>KPI Score (Color):</span>
                    <span className="font-medium text-right text-slate-500">
                      –
                    </span>
                  </div>
                </div>
              )}
              {isMetricView && (
                <div className="space-y-1 text-xs text-muted-foreground border-t border-border pt-1.5">
                  <div className="flex items-start justify-between gap-3">
                    <span>Normalized Score:</span>
                    <span className="font-medium text-right text-slate-500">
                      –
                    </span>
                  </div>
                </div>
              )}
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  const hasAiScore = aiScore !== undefined;
  const divergenceGap = hasAiScore ? Math.abs(numericScore - aiScore) : 0;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>{bubbleContent}</TooltipTrigger>
        <TooltipContent
          side="top"
          align="center"
          sideOffset={10}
          className="w-72 max-w-[90vw] overflow-visible bg-white text-slate-900 p-3 border z-[120] break-words leading-relaxed"
          style={{ boxShadow: getBubbleGlow() }}
        >
          <div className="space-y-2">
            {/* Header */}
            <div className="text-center">
              <div className={`text-lg font-bold ${getScoreColorClass()}`}>
                {Math.round(numericScore)}
              </div>
              {tooltipTitle && (
                <div className="text-sm font-semibold text-foreground">
                  {tooltipTitle}
                </div>
              )}
            </div>

            {/* Dual score display - only for non-metric-view */}
            {!isMetricView && (
              <div className="space-y-1 text-xs text-muted-foreground border-t border-border pt-1.5">
                <div className="flex items-start justify-between gap-3">
                  <span>KPI Score (Color):</span>
                  <span
                    className={`font-medium text-right ${getScoreColorClass()}`}
                  >
                    {Math.round(numericScore)}
                  </span>
                </div>
                {hasAiScore && (
                  <div className="flex items-start justify-between gap-3">
                    <span>
                      {isOverall ? "AI Assessment:" : "AI Assessment (Size):"}
                    </span>
                    <span className="font-medium text-right">
                      {Math.round(aiScore)}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Metric-view details: normalized value, actual value + UOM, weight */}
            {isMetricView && metrics.length > 0 && (
              <div className="space-y-1 text-xs text-muted-foreground border-t border-border pt-1.5">
                {metrics.map((metric, index) => (
                  <React.Fragment key={index}>
                    <div className="flex items-start justify-between gap-3">
                      <span>Normalized Score:</span>
                      <span
                        className={`font-medium text-right ${getScoreColorClass()}`}
                      >
                        {metric.value.toFixed(1)}
                      </span>
                    </div>
                    {metric.actualValue !== undefined && (
                      <div className="flex items-start justify-between gap-3">
                        <span>Actual Value:</span>
                        <span className="font-medium text-right">
                          {formatActualValueWithUnit(
                            metric.actualValue,
                            metric.uom
                          )}
                        </span>
                      </div>
                    )}
                    <div className="flex items-start justify-between gap-3">
                      <span>Weight:</span>
                      <span className="font-medium text-right">
                        {metric.weight.toFixed(0)}%
                      </span>
                    </div>
                  </React.Fragment>
                ))}
              </div>
            )}

            {/* Divergence warning */}
            {divergenceWarning && hasAiScore && (
              <div className="flex items-center gap-1.5 text-xs text-amber-600 bg-amber-50 rounded px-2 py-1">
                <AlertTriangle className="h-3 w-3 flex-shrink-0" />
                <span>
                  AI assessment diverges from KPI metrics (
                  {Math.round(divergenceGap)}pt gap)
                </span>
              </div>
            )}

            {/* Metrics breakdown for category cells (non-metric-view only) */}
            {!isMetricView && metrics.length > 0 && (
              <div className="space-y-1">
                {metrics.map((metric, index) => (
                  <div key={index} className="text-xs text-muted-foreground">
                    {metric.name}: {metric.value.toFixed(2)} (Weight:{" "}
                    {metric.weight.toFixed(0)}%)
                  </div>
                ))}
              </div>
            )}

            {/* Categories breakdown for overall cells */}
            {categories.length > 0 && (
              <div className="space-y-1">
                {categories.map((category, index) => (
                  <div key={index} className="text-xs text-muted-foreground">
                    {category.name}: {Math.round(category.score)} (Weight:{" "}
                    {category.weight.toFixed(0)}%)
                  </div>
                ))}
              </div>
            )}

            {/* Zero-value note */}
            {isZeroScore && (
              <div className="text-xs text-muted-foreground italic pt-1 border-t border-border">
                Value is zero for this period.
              </div>
            )}

            {/* No AI data note - category bubbles only (overall uses full-size disk) */}
            {!isZeroScore &&
              !isMetricView &&
              !isOverall &&
              !hasAiScore &&
              !hasDigest && (
              <div className="text-xs text-muted-foreground/60 italic pt-1 border-t border-border">
                No AI score available — size reflects KPI score
              </div>
            )}

            {/* Click hint - differs between metric view and category view */}
            {/* {isMetricView ? (
              <div className="text-xs text-muted-foreground italic pt-1 border-t border-border">
                Detailed AI analysis is available in Categories view.
              </div>
            ) : hasDigest ? (
               null
            ) : null} */}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
